/*
段落、箇条書き、表のセルの最後の 4 字を、折り返さない 1 つの塊 (span) にする。
text-wrap: pretty は和文では 2〜3 字だけの最後の行 (「ます。」など) を避けきれないので、末尾を塊にして、最後の行が短くならないようにする。
見た目 (折り返さない) は lg-prose.css が付ける。lg の外では span は何も変えない。
末尾の 4 字が和文 (と閉じ括弧、語の一部の英数字、短いコード) のものだけが対象。リンクなどの部品にかかる末尾、短い文 (6 字以下)、
末尾 4 字に開き括弧や引用符を含む文、【 で始まる文 (対談の話者の行) は触らない。
末尾 4 字が蛍光ペンや太字 (mark、strong など) の中、またはその境目をまたぐときは、その要素を境目で 2 つに分け、
塊の中に同じ要素の続きを入れる (属性の無い要素と ==字== の蛍光ペンだけ。id、style、class などの属性を持つ要素は分けず、境目をまたぐ文は包まない) (「==大丈夫です==。」の「。」だけが最後の行に落ちるのを防ぐ)。字は 1 字も変えない。
*/

// 末尾の 4 字に入ってよい字。和文と、英数字 (「properties のキー」の s など、語の一部)、閉じ括弧と閉じ引用符
const TAIL_CHAR = /[　-ヿ㐀-䶿一-鿿＀-￯A-Za-z0-9)\]]/;
// 末尾に和文の字か句読点が 1 字は要る (英数字だけで終わる文は触らない)
const TAIL_CJK = /[　-ヿ㐀-䶿一-鿿＀-￯]/;
// 末尾の 4 字に開き括弧や開き引用符、中黒を含む文は触らない (「」の対応や、話者の記号 【代表】 の判定を壊さない)。閉じ括弧は行頭に来ない決まりなので、塊に入れてよい
const OPENERS = /[【「『（(〈《［[｢"“'‘・]/;
const TAIL_LENGTH = 4;
const MIN_LENGTH = 7;
const BLOCKS = new Set(["p", "li", "td", "th"]);
const SKIP = new Set(["pre", "code", "kbd", "svg", "style", "script", "textarea"]);
// 末尾の塊が中にかかってよい、字だけを包む要素。境目で 2 つに分けても意味が変わらない
const SPLITTABLE = new Set(["mark", "strong", "em", "b", "i", "u", "del", "s"]);
// 分けない部品。末尾の 4 字にかかるときは、丸ごと塊に入れる (「終了は \q」の \q だけが最後の行に落ちない)。長いコードは入れない
const ATOMS = new Set(["code", "kbd"]);
const ATOM_MAX = 12;

type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

/**
 * 文字列だけの段落 (リンクカードの説明など、Markdown を通らない字) の最後の 4 字を分ける。
 * 対象の決め方は rehypeKeepTail と同じ。対象外 (短い、和文が無い、末尾に開き括弧や空白がある) なら null
 */
export function splitKeepTail(text: string): [string, string] | null {
  if (text.length < MIN_LENGTH) return null;
  const tail = text.slice(-TAIL_LENGTH);
  let cjk = 0;
  for (const ch of tail) {
    if (!TAIL_CHAR.test(ch) || OPENERS.test(ch)) return null;
    if (TAIL_CJK.test(ch)) cjk += 1;
  }
  return cjk >= 1 ? [text.slice(0, -TAIL_LENGTH), tail] : null;
}

function textLength(node: HastNode): number {
  if (node.type === "text") return (node.value ?? "").length;
  return (node.children ?? []).reduce((sum, child) => sum + textLength(child), 0);
}

function textOf(node: HastNode): string {
  if (node.type === "text") return node.value ?? "";
  return (node.children ?? []).map(textOf).join("");
}

function textStart(node: HastNode): string {
  if (node.type === "text") return (node.value ?? "").trimStart().slice(0, 1);
  for (const child of node.children ?? []) {
    const t = textStart(child);
    if (t) return t;
  }
  return "";
}

// 分けてよい要素か。属性が 1 つも無いか、==字== から作る蛍光ペンの印 (class="highlight-green" だけ。装飾の規則を持たない) だけ
function isPlain(node: HastNode): boolean {
  const props = node.properties ?? {};
  const keys = Object.keys(props);
  if (keys.length === 0) return true;
  if (node.tagName !== "mark" || keys.length !== 1 || keys[0] !== "className") return false;
  const classes = props.className;
  return Array.isArray(classes) && classes.length === 1 && classes[0] === "highlight-green";
}

type Parts = { before: HastNode | null; inside: HastNode | null; after: HastNode | null };

function withChildren(node: HastNode, children: HastNode[]): HastNode | null {
  return children.length > 0 ? { ...node, children } : null;
}

// node の字のうち、ブロックの先頭から数えて [from, to) にあたる部分を、前、中、後ろの 3 つに分ける。範囲にかかる部品が分けられないもの (コード、リンクなど) なら null
function splitRange(node: HastNode, pos: number, from: number, to: number): Parts | null {
  const end = pos + textLength(node);
  if (end <= from) return { before: node, inside: null, after: null };
  if (pos >= to) return { before: null, inside: null, after: node };
  if (node.type === "text") {
    const value = node.value ?? "";
    const a = Math.max(from - pos, 0);
    const b = Math.min(to - pos, value.length);
    const piece = (s: string): HastNode | null => (s ? { ...node, value: s } : null);
    return { before: piece(value.slice(0, a)), inside: piece(value.slice(a, b)), after: piece(value.slice(b)) };
  }
  if (node.type === "element" && ATOMS.has(node.tagName ?? "")) {
    return pos >= from && end <= to ? { before: null, inside: node, after: null } : null;
  }
  if (node.type !== "element" || !SPLITTABLE.has(node.tagName ?? "")) return null;
  // 属性を 1 つでも持つ要素 (id、style、class、data-*、title など) は分けない。分けると同じ id や余白や枠が両方に付く。範囲に全部入るときだけそのまま中へ入れる
  if (!isPlain(node)) {
    return pos >= from && end <= to ? { before: null, inside: node, after: null } : null;
  }
  const before: HastNode[] = [];
  const inside: HastNode[] = [];
  const after: HastNode[] = [];
  let cursor = pos;
  for (const child of node.children ?? []) {
    const parts = splitRange(child, cursor, from, to);
    if (!parts) return null;
    if (parts.before) before.push(parts.before);
    if (parts.inside) inside.push(parts.inside);
    if (parts.after) after.push(parts.after);
    cursor += textLength(child);
  }
  return { before: withChildren(node, before), inside: withChildren(node, inside), after: withChildren(node, after) };
}

type Segment = { start: number; end: number; atom: boolean; text: string };

// 字のある部分を、先頭からの位置つきで並べる。コードは分けない 1 つの部分にする
function segmentsOf(node: HastNode, pos: number, out: Segment[]): number {
  if (node.type === "text") {
    const value = node.value ?? "";
    out.push({ start: pos, end: pos + value.length, atom: false, text: value });
    return pos + value.length;
  }
  if (node.type === "element" && ATOMS.has(node.tagName ?? "")) {
    const text = textOf(node);
    out.push({ start: pos, end: pos + text.length, atom: true, text });
    return pos + text.length;
  }
  let cursor = pos;
  for (const child of node.children ?? []) cursor = segmentsOf(child, cursor, out);
  return cursor;
}

function wrapTail(block: HastNode): void {
  const full = textOf(block);
  if (full.length < MIN_LENGTH || textStart(block) === "【") return;
  // 末尾の空白を後ろから 1 回だけ数える (正規表現の /\s+$/ は、途中に長い空白列があると二次時間になる)
  let to = full.length;
  while (to > 0 && /\s/.test(full[to - 1])) to -= 1;
  const segments: Segment[] = [];
  segmentsOf(block, 0, segments);
  // 末尾から 4 字 (空白は数えない。コードは丸ごと 1 つ) をたどる
  let count = 0;
  let from = to;
  for (let i = segments.length - 1; i >= 0 && count < TAIL_LENGTH; i--) {
    const seg = segments[i];
    if (seg.start >= to) continue;
    if (seg.atom) {
      if (seg.end > to || seg.end - seg.start > ATOM_MAX) return;
      count += seg.end - seg.start;
      from = seg.start;
      continue;
    }
    for (let k = Math.min(seg.end, to) - 1; k >= seg.start && count < TAIL_LENGTH; k--) {
      from = k;
      if (!/\s/.test(seg.text[k - seg.start])) count += 1;
    }
  }
  if (count < TAIL_LENGTH) return;
  let cjk = 0;
  for (const seg of segments) {
    if (seg.atom || seg.end <= from || seg.start >= to) continue;
    for (const ch of seg.text.slice(Math.max(from - seg.start, 0), to - seg.start)) {
      if (/\s/.test(ch)) continue;
      if (!TAIL_CHAR.test(ch) || OPENERS.test(ch)) return;
      if (TAIL_CJK.test(ch)) cjk += 1;
    }
  }
  if (cjk < 1) return;
  const before: HastNode[] = [];
  const inside: HastNode[] = [];
  const after: HastNode[] = [];
  let cursor = 0;
  for (const child of block.children ?? []) {
    const parts = splitRange(child, cursor, from, to);
    if (!parts) return;
    if (parts.before) before.push(parts.before);
    if (parts.inside) inside.push(parts.inside);
    if (parts.after) after.push(parts.after);
    cursor += textLength(child);
  }
  const tail: HastNode = {
    type: "element",
    tagName: "span",
    properties: { className: ["md-tail"] },
    children: inside,
  };
  block.children = [...before, tail, ...after];
}

function visit(node: HastNode): void {
  if (!node.children) return;
  if (node.type === "element" && SKIP.has(node.tagName ?? "")) return;
  if (node.type === "element" && BLOCKS.has(node.tagName ?? "")) wrapTail(node);
  for (const child of [...node.children]) visit(child);
}

export function rehypeKeepTail() {
  return (tree: HastNode) => {
    visit(tree);
  };
}
